import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Settings, Menu, X, LogOut, LogIn, ChevronDown, Sun, Moon } from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/lib/auth-store";
import { useTheme } from "@/lib/theme";
import { useNavigationMenuByLocation, usePages, useTags, usePagesByTag } from "@/lib/api";
import type { NavigationItem } from "@shared/schema";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export function Header() {
  const [location] = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { data: headerMenu } = useNavigationMenuByLocation("header");
  const { data: pages = [] } = usePages();
  const { data: tags = [] } = useTags();
  const reviewsTag = tags.find(t => t.name === "Reviews");
  const { data: reviewsPages = [] } = usePagesByTag(reviewsTag?.id || "");
  const publishedReviewsPages = reviewsPages.filter(p => p.isPublished);
  
  // Get menu items
  const menuItems: NavigationItem[] = headerMenu?.items || [];
  const topLevelItems = menuItems.filter(item => !item.parentId);
  
  const { resolvedTheme, setTheme } = useTheme();
  
  const toggleTheme = () => {
    setTheme(resolvedTheme === "dark" ? "light" : "dark");
  };

  const auth = useAuth();
  const isAuthenticated = auth?.isAuthenticated;
  const logout = auth?.logout;
  
  const getItemUrl = (item: NavigationItem) => {
    if (item.url) return item.url;
    if (item.pageId) {
      const page = pages.find(p => p.id === item.pageId);
      if (page) return `/page/${page.slug}`;
    }
    return "#";
  };
  
  const getChildItems = (parentId: string) => {
    return menuItems.filter(item => item.parentId === parentId);
  };

  const isReviewsItem = (item: NavigationItem) => {
    return item.label === "Reviews" && publishedReviewsPages.length > 0;
  };

  const getPageUrl = (page: { slug: string; pageType: string }) => {
    if (page.pageType === "custom") return `/page/${page.slug}`;
    return `/${page.slug}`;
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container mx-auto px-4">
        <div className="flex h-16 items-center justify-between">
          <Link href="/" className="flex items-center hover:opacity-80 transition-opacity">
            <span className="font-display font-bold text-xl tracking-tight" data-testid="logo-text">
              Top Breakdown Cover
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            <Link href="/">
              <Button
                size="sm"
                className="bg-green-600 hover:bg-green-700 text-white"
                data-testid="nav-compare"
              >
                Compare
              </Button>
            </Link>
            <Link href="/cover-finder">
              <Button
                variant={location === "/cover-finder" ? "secondary" : "ghost"}
                size="sm"
                data-testid="nav-cover-finder"
              >
                Cover Finder
              </Button>
            </Link>
            <Link href="/blog">
              <Button
                variant={location === "/blog" || location.startsWith("/blog/") ? "secondary" : "ghost"}
                size="sm"
                data-testid="nav-blog"
              >
                Blog
              </Button>
            </Link>
            <Link href="/car-insurance">
              <Button
                variant={location === "/car-insurance" ? "secondary" : "ghost"}
                size="sm"
                data-testid="nav-car-insurance"
              >
                Car Insurance
              </Button>
            </Link>
            
            {headerMenu?.name && (menuItems.length > 0 || publishedReviewsPages.length > 0) && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="gap-1" data-testid="nav-reviews">
                    {headerMenu.name}
                    <ChevronDown className="w-3 h-3" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  {publishedReviewsPages.length > 0 && menuItems.length === 0 && (
                    publishedReviewsPages.map((page) => (
                      <DropdownMenuItem key={page.id} asChild>
                        <Link href={getPageUrl(page)}>
                          {page.title}
                        </Link>
                      </DropdownMenuItem>
                    ))
                  )}
                  {topLevelItems.map((item) => {
                    const children = getChildItems(item.id);
                    const itemUrl = getItemUrl(item);
                    
                    if (isReviewsItem(item)) {
                      return publishedReviewsPages.map((page) => (
                        <DropdownMenuItem key={page.id} asChild>
                          <Link href={getPageUrl(page)}>
                            {page.title}
                          </Link>
                        </DropdownMenuItem>
                      ));
                    }

                    return (
                      <div key={item.id}>
                        <DropdownMenuItem asChild>
                          <Link href={itemUrl} target={item.openInNewTab ? "_blank" : undefined}>
                            {item.label}
                          </Link>
                        </DropdownMenuItem>
                        {children.map((child) => (
                          <DropdownMenuItem key={child.id} asChild className="pl-6">
                            <Link href={getItemUrl(child)}>
                              {child.label}
                            </Link>
                          </DropdownMenuItem>
                        ))}
                      </div>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            
            {!headerMenu?.name && topLevelItems.map((item) => {
              const children = getChildItems(item.id);
              const itemUrl = getItemUrl(item);
              
              if (isReviewsItem(item)) {
                return (
                  <DropdownMenu key={item.id}>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="gap-1" data-testid="nav-reviews">
                        {item.label}
                        <ChevronDown className="w-3 h-3" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                      {publishedReviewsPages.map((page) => (
                        <DropdownMenuItem key={page.id} asChild>
                          <Link href={getPageUrl(page)}>
                            {page.title}
                          </Link>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                );
              }

              if (children.length > 0) {
                return (
                  <DropdownMenu key={item.id}>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="gap-1">
                        {item.label}
                        <ChevronDown className="w-3 h-3" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                      {children.map((child) => (
                        <DropdownMenuItem key={child.id} asChild>
                          <Link href={getItemUrl(child)}>
                            {child.label}
                          </Link>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                );
              }
              
              return (
                <Link key={item.id} href={itemUrl} target={item.openInNewTab ? "_blank" : undefined}>
                  <Button
                    variant="ghost"
                    size="sm"
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {item.label}
                  </Button>
                </Link>
              );
            })}
            
            {isAuthenticated && (
              <>
                <Link href="/admin">
                  <Button
                    variant="ghost"
                    size="sm"
                    data-testid="nav-admin"
                  >
                    <Settings className="w-4 h-4 mr-1.5" />
                    Admin
                  </Button>
                </Link>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => logout && logout()}
                  className="text-muted-foreground hover:text-foreground"
                  data-testid="nav-logout"
                >
                  <LogOut className="w-4 h-4 mr-1.5" />
                  Logout
                </Button>
              </>
            )}
          </nav>

          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              data-testid="theme-toggle"
              aria-label={resolvedTheme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
              className="h-9 w-9"
            >
              {resolvedTheme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {mobileMenuOpen && (
          <nav className="md:hidden py-4 border-t flex flex-col gap-2">
            <Link href="/" onClick={() => setMobileMenuOpen(false)}>
              <Button
                variant={location === "/" ? "secondary" : "ghost"}
                className="w-full justify-start"
                data-testid="mobile-nav-compare"
              >
                Compare Providers
              </Button>
            </Link>
            <Link href="/cover-finder" onClick={() => setMobileMenuOpen(false)}>
              <Button
                variant={location === "/cover-finder" ? "secondary" : "ghost"}
                className="w-full justify-start"
                data-testid="mobile-nav-cover-finder"
              >
                Cover Finder
              </Button>
            </Link>
            <Link href="/blog" onClick={() => setMobileMenuOpen(false)}>
              <Button
                variant={location === "/blog" || location.startsWith("/blog/") ? "secondary" : "ghost"}
                className="w-full justify-start"
                data-testid="mobile-nav-blog"
              >
                Blog
              </Button>
            </Link>
            <Link href="/car-insurance" onClick={() => setMobileMenuOpen(false)}>
              <Button
                variant={location === "/car-insurance" ? "secondary" : "ghost"}
                className="w-full justify-start"
                data-testid="mobile-nav-car-insurance"
              >
                Car Insurance
              </Button>
            </Link>
            
            {headerMenu?.name && (menuItems.length > 0 || publishedReviewsPages.length > 0) && (
              <div className="border-t pt-2 mt-2">
                <p className="px-4 py-2 text-sm font-medium text-muted-foreground">{headerMenu.name}</p>
                {publishedReviewsPages.length > 0 && menuItems.length === 0 && (
                  publishedReviewsPages.map((page) => (
                    <Link key={page.id} href={getPageUrl(page)} onClick={() => setMobileMenuOpen(false)}>
                      <Button
                        variant="ghost"
                        className="w-full justify-start pl-6"
                      >
                        {page.title}
                      </Button>
                    </Link>
                  ))
                )}
                {topLevelItems.map((item) => {
                  const children = getChildItems(item.id);
                  const itemUrl = getItemUrl(item);
                  
                  return (
                    <div key={item.id}>
                      <Link href={itemUrl} onClick={() => setMobileMenuOpen(false)} target={item.openInNewTab ? "_blank" : undefined}>
                        <Button
                          variant="ghost"
                          className="w-full justify-start pl-6"
                        >
                          {item.label}
                        </Button>
                      </Link>
                      {children.map((child) => (
                        <Link key={child.id} href={getItemUrl(child)} onClick={() => setMobileMenuOpen(false)}>
                          <Button
                            variant="ghost"
                            className="w-full justify-start pl-10 text-muted-foreground"
                          >
                            {child.label}
                          </Button>
                        </Link>
                      ))}
                    </div>
                  );
                })}
              </div>
            )}
            
            {!headerMenu?.name && topLevelItems.map((item) => {
              const children = getChildItems(item.id);
              const itemUrl = getItemUrl(item);
              
              if (isReviewsItem(item)) {
                return (
                  <div key={item.id}>
                    <p className="px-4 py-2 text-sm font-medium text-muted-foreground">{item.label}</p>
                    {publishedReviewsPages.map((page) => (
                      <Link key={page.id} href={getPageUrl(page)} onClick={() => setMobileMenuOpen(false)}>
                        <Button
                          variant="ghost"
                          className="w-full justify-start pl-8"
                        >
                          {page.title}
                        </Button>
                      </Link>
                    ))}
                  </div>
                );
              }

              return (
                <div key={item.id}>
                  <Link href={itemUrl} onClick={() => setMobileMenuOpen(false)} target={item.openInNewTab ? "_blank" : undefined}>
                    <Button
                      variant="ghost"
                      className="w-full justify-start"
                    >
                      {item.label}
                    </Button>
                  </Link>
                  {children.map((child) => (
                    <Link key={child.id} href={getItemUrl(child)} onClick={() => setMobileMenuOpen(false)}>
                      <Button
                        variant="ghost"
                        className="w-full justify-start pl-8 text-muted-foreground"
                      >
                        {child.label}
                      </Button>
                    </Link>
                  ))}
                </div>
              );
            })}
            
            {isAuthenticated && (
              <>
                <Link href="/admin" onClick={() => setMobileMenuOpen(false)}>
                  <Button
                    variant="ghost"
                    className="w-full justify-start"
                    data-testid="mobile-nav-admin"
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Admin
                  </Button>
                </Link>
                <Button
                  variant="ghost"
                  className="w-full justify-start text-muted-foreground"
                  onClick={() => {
                    logout && logout();
                    setMobileMenuOpen(false);
                  }}
                  data-testid="mobile-nav-logout"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </Button>
              </>
            )}
          </nav>
        )}
      </div>
    </header>
  );
}
